import React from "react";
import { CButton, CFormCheck, CFormInput, CFormTextarea } from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { Formik } from "formik";
import { userRestriction } from "@/constants/translation/userRestriction.translation";

const tr = userRestriction;
const REASON_MAX_LENGTH = 500;

// Local "YYYY-MM-DD" for a Date, the format <input type="date"> uses.
const toDateInputValue = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const tomorrow = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date;
};

// The picked day counts through its last minute, in the admin's local time.
const endOfDayIso = (dateValue) => new Date(`${dateValue}T23:59:00`).toISOString();

const PopupShell = ({ onClose, title, children }) => (
  <div className="alert-box admin-status-popup">
    <div className="alert-popup">
      <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
      <h2 className="alert-title">{title}</h2>
      {children}
    </div>
  </div>
);

/**
 * Restrict popup: required reason (shown to the user) and either an end date or "Until lifted manually".
 * @param {Object} params
 * @param {function} params.t - Translation function.
 * @param {function} params.onConfirm - Called with { reason, end_date } or { reason, until_manual: true }.
 */
export const showRestrictDialog = ({ t, onConfirm }) => {
  confirmAlert({
    customUI: ({ onClose }) => (
      <Formik
        initialValues={{ reason: "", endDate: "", untilManual: false }}
        validate={(values) => {
          const errors = {};
          if (!values.reason.trim()) errors.reason = t(tr.restrict_reason_required);
          if (!values.untilManual) {
            if (!values.endDate) errors.endDate = t(tr.restrict_end_required);
            else if (values.endDate < toDateInputValue(new Date())) errors.endDate = t(tr.restrict_end_past);
          }
          return errors;
        }}
        onSubmit={(values, { setSubmitting }) => {
          const reason = values.reason.trim();
          onConfirm(
            values.untilManual
              ? { reason, until_manual: true }
              : { reason, end_date: endOfDayIso(values.endDate) }
          );
          setSubmitting(false);
          onClose();
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting }) => (
          <PopupShell onClose={onClose} title={t(tr.restrict_heading)}>
            <p className="text-center mb-3">{t(tr.restrict_message)}</p>
            <div className="mb-3 text-start">
              <label htmlFor="reason" className="form-label">
                {t(tr.restrict_reason_label)} *
              </label>
              <CFormTextarea
                id="reason"
                name="reason"
                rows={3}
                maxLength={REASON_MAX_LENGTH}
                placeholder={t(tr.restrict_reason_placeholder)}
                value={values.reason}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {touched.reason && errors.reason && <div className="text-danger small mt-1">{errors.reason}</div>}
            </div>
            <div className="mb-3 text-start">
              <label htmlFor="endDate" className="form-label">
                {t(tr.restrict_end_label)}
              </label>
              <CFormInput
                id="endDate"
                name="endDate"
                type="date"
                min={toDateInputValue(tomorrow())}
                disabled={values.untilManual}
                value={values.endDate}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <CFormCheck
                className="mt-2"
                id="untilManual"
                label={t(tr.restrict_until_manual)}
                checked={values.untilManual}
                onChange={(e) => {
                  setFieldValue("untilManual", e.target.checked);
                  if (e.target.checked) setFieldValue("endDate", "");
                }}
              />
              {touched.endDate && errors.endDate && <div className="text-danger small mt-1">{errors.endDate}</div>}
            </div>
            <div className="d-flex gap-2 justify-content-center flex-nowrap">
              <CButton
                color="primary"
                className="table-delete-button"
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                {t(tr.restrict_submit)}
              </CButton>
              <CButton className="table-primary-button" color="danger" onClick={onClose} disabled={isSubmitting}>
                {t(tr.restrict_cancel)}
              </CButton>
            </div>
          </PopupShell>
        )}
      </Formik>
    ),
  });
};

/**
 * Delete popup: "Are you sure? This cannot be undone." plus an optional reason.
 * @param {Object} params
 * @param {function} params.t - Translation function.
 * @param {function} params.onConfirm - Called with the trimmed reason ("" when left empty).
 */
export const showDeleteDialog = ({ t, onConfirm }) => {
  confirmAlert({
    customUI: ({ onClose }) => (
      <Formik
        initialValues={{ reason: "" }}
        onSubmit={(values, { setSubmitting }) => {
          onConfirm(values.reason.trim());
          setSubmitting(false);
          onClose();
        }}
      >
        {({ values, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
          <PopupShell onClose={onClose} title={t(tr.delete_heading)}>
            <p className="text-center mb-3">{t(tr.delete_message)}</p>
            <div className="mb-3 text-start">
              <CFormTextarea
                id="reason"
                name="reason"
                rows={3}
                maxLength={REASON_MAX_LENGTH}
                placeholder={t(tr.delete_reason_placeholder)}
                value={values.reason}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
            <div className="d-flex gap-2 justify-content-center flex-nowrap">
              <CButton color="primary" className="table-delete-button" disabled={isSubmitting} onClick={handleSubmit}>
                {t(tr.delete_submit)}
              </CButton>
              <CButton className="table-primary-button" color="danger" onClick={onClose} disabled={isSubmitting}>
                {t(tr.restrict_cancel)}
              </CButton>
            </div>
          </PopupShell>
        )}
      </Formik>
    ),
  });
};
